import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useCurrentUser } from "@/hooks/use-current-user";
import {
  readPreviousTags,
  writePreviousTags,
  type PreviousTagsEntry,
} from "@/lib/previous-tags";
import { recordKeys } from "@/lib/query-keys";
import type { PaginatedResponse, ProjectRecord, ProjectTag } from "@/types/api";

export interface PreviousTagsState {
  enabled: boolean;
  /** Remembered ids, already narrowed to tags that still exist. */
  tagIds: string[];
  setEnabled: (next: boolean) => void;
  /** Overwrite the remembered set with the tags of a record just created. */
  remember: (tagIds: string[]) => void;
}

/**
 * Pull `ProjectRecord[]` out of whatever sits under a `recordKeys.byProject`
 * cache entry. That prefix covers both the infinite list (`{ pages: [...] }`)
 * and `recordKeys.detail` (a bare record), so the shape is checked rather than
 * assumed.
 */
function recordsFromCacheEntry(data: unknown): ProjectRecord[] {
  if (!data || typeof data !== "object") return [];
  const pages = (data as { pages?: unknown }).pages;
  if (Array.isArray(pages)) {
    return pages.flatMap((page) => {
      const results = (page as Partial<PaginatedResponse<ProjectRecord>>)
        ?.results;
      return Array.isArray(results) ? results : [];
    });
  }
  const record = data as Partial<ProjectRecord>;
  return Array.isArray(record.tags) && record.created_by
    ? [record as ProjectRecord]
    : [];
}

/**
 * The tags of the newest **already-cached** record this user created in this
 * project. Used only as a cold-start fallback when the device has no stored
 * set — a fresh browser, or a first capture after clearing site data.
 *
 * Cache-only on purpose. Record creation is offline-first, so a network read
 * here would fail exactly when a field worker is most likely to be offline;
 * if nothing is cached the toggle simply doesn't appear. The creator match is
 * also what keeps another user's tags out: `created_by.id` is the backend user
 * id, the same id space as `useCurrentUser().data.id`.
 */
function newestOwnRecordTags(
  entries: [unknown, unknown][],
  userId: string,
): string[] {
  let newest: ProjectRecord | null = null;
  for (const [, data] of entries) {
    for (const record of recordsFromCacheEntry(data)) {
      if (record.created_by?.id !== userId) continue;
      if (record.tags.length === 0) continue;
      if (
        !newest ||
        (record.created_at ?? "") > (newest.created_at ?? "")
      ) {
        newest = record;
      }
    }
  }
  return newest?.tags ?? [];
}

/**
 * The "Use previous tags" memory for one user in one project.
 *
 * Everything is gated on a resolved user id — with none, the hook reports
 * unavailable and neither reads nor writes, so a shared device can never leak
 * one person's tags to the next. See [[image-viewer-modal.spec]].
 */
export function useProjectPreviousTags(
  projectId: string,
  projectTags: ProjectTag[],
): PreviousTagsState {
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.id ?? null;
  const queryClient = useQueryClient();

  // Seeded synchronously so a pre-filled selection is present on the form's
  // very first render rather than arriving a frame later. `useCurrentUser` is
  // an offline-durable persisted query and has resolved long before the
  // capture modal mounts, so the lazy read is the normal path; the effect
  // below covers the case where it hadn't.
  const [entry, setEntry] = useState<PreviousTagsEntry | null>(() =>
    userId ? readPreviousTags(projectId, userId) : null,
  );

  // Re-read when the (project, user) pair changes — the "adjust state during
  // render" pattern rather than an effect, so the new scope's value is right on
  // the first render for it instead of one render late. Guarded on the scope
  // string so it never clobbers a fresh `remember()` write.
  const scope = userId ? `${projectId}:${userId}` : null;
  const [loadedScope, setLoadedScope] = useState<string | null>(scope);
  if (scope !== loadedScope) {
    setLoadedScope(scope);
    setEntry(userId ? readPreviousTags(projectId, userId) : null);
  }

  const validIds = useMemo(
    () => new Set(projectTags.map((tag) => tag.id)),
    [projectTags],
  );

  const tagIds = useMemo(() => {
    if (!userId) return [];
    const source =
      entry?.tagIds ??
      newestOwnRecordTags(
        queryClient.getQueriesData({
          queryKey: recordKeys.byProject(projectId),
        }),
        userId,
      );
    // A tag deleted from the project must never come back through the memory.
    return source.filter((id) => validIds.has(id));
  }, [entry, userId, projectId, queryClient, validIds]);

  const enabled = entry?.enabled ?? false;

  const persist = useCallback(
    (next: Omit<PreviousTagsEntry, "updatedAt">) => {
      if (!userId) return;
      writePreviousTags(projectId, userId, next);
      setEntry({ ...next, updatedAt: Date.now() });
    },
    [projectId, userId],
  );

  const setEnabled = useCallback(
    // Flipping the switch on from the cache fallback also commits those ids,
    // so the set stops depending on what happens to be cached.
    (next: boolean) => persist({ enabled: next, tagIds }),
    [persist, tagIds],
  );

  const remember = useCallback(
    // Rolling: the record just created defines "previous", tags or no tags.
    (nextTagIds: string[]) => persist({ enabled, tagIds: nextTagIds }),
    [persist, enabled],
  );

  // No `available` flag: emptiness is `tagIds.length === 0`, and the toggle
  // renders either way — an inert switch that says why beats one that
  // vanishes. With no user id `tagIds` is empty and the writers no-op, so the
  // unresolved-identity case lands in the same inert state.
  return { enabled, tagIds, setEnabled, remember };
}
